package kpt.core.database

import androidx.room3.InvalidationTracker
import androidx.room3.RoomOpenDelegate
import androidx.room3.migration.AutoMigrationSpec
import androidx.room3.migration.Migration
import androidx.room3.util.TableInfo
import androidx.room3.util.TableInfo.Companion.read
import androidx.room3.util.dropFtsSyncTriggers
import androidx.room3.util.performClear
import androidx.sqlite.SQLiteConnection
import androidx.sqlite.execSQL
import kotlin.Lazy
import kotlin.String
import kotlin.Suppress
import kotlin.collections.List
import kotlin.collections.Map
import kotlin.collections.MutableList
import kotlin.collections.MutableMap
import kotlin.collections.MutableSet
import kotlin.collections.Set
import kotlin.collections.mutableListOf
import kotlin.collections.mutableMapOf
import kotlin.collections.mutableSetOf
import kotlin.reflect.KClass
import kpt.core.base.database.infra.dao.BookkeeperDao
import kpt.core.base.database.infra.dao.BookkeeperDao_Impl
import kpt.core.base.database.infra.dao.ConflictDao
import kpt.core.base.database.infra.dao.ConflictDao_Impl
import kpt.core.base.database.infra.dao.DraftDao
import kpt.core.base.database.infra.dao.DraftDao_Impl
import kpt.core.base.database.infra.dao.FetchedAtDao
import kpt.core.base.database.infra.dao.FetchedAtDao_Impl
import kpt.core.database.alerts.AlertDao
import kpt.core.database.alerts.AlertDao_Impl
import kpt.core.database.banking.dao.BillReminderDao
import kpt.core.database.banking.dao.BillReminderDao_Impl
import kpt.core.database.banking.dao.LoanDao
import kpt.core.database.banking.dao.LoanDao_Impl
import kpt.core.database.cloudtodo.CloudTodoDao
import kpt.core.database.cloudtodo.CloudTodoDao_Impl
import kpt.core.database.crypto.dao.CoinDetailDao
import kpt.core.database.crypto.dao.CoinDetailDao_Impl
import kpt.core.database.crypto.dao.CoinMarketDao
import kpt.core.database.crypto.dao.CoinMarketDao_Impl
import kpt.core.database.currency.dao.ExchangeRatesDao
import kpt.core.database.currency.dao.ExchangeRatesDao_Impl
import kpt.core.database.currency.dao.RateHistoryDao
import kpt.core.database.currency.dao.RateHistoryDao_Impl
import kpt.core.database.economic.InterestRateSeriesDao
import kpt.core.database.economic.InterestRateSeriesDao_Impl
import kpt.core.database.watchlist.dao.WatchlistDao
import kpt.core.database.watchlist.dao.WatchlistDao_Impl

@Suppress(names = ["UNCHECKED_CAST", "DEPRECATION", "REDUNDANT_PROJECTION", "REMOVAL", "OPT_IN_USAGE_ERROR", "OPT_IN_USAGE", "MemberExtensionConflict", "CAN_BE_VAL"])
internal class AppDatabase_Impl : AppDatabase() {
  private val _bookkeeperDao: Lazy<BookkeeperDao> = lazy {
    BookkeeperDao_Impl(this)
  }

  public override val bookkeeperDao: BookkeeperDao
    get() = _bookkeeperDao.value

  private val _fetchedAtDao: Lazy<FetchedAtDao> = lazy {
    FetchedAtDao_Impl(this)
  }

  public override val fetchedAtDao: FetchedAtDao
    get() = _fetchedAtDao.value

  private val _draftDao: Lazy<DraftDao> = lazy {
    DraftDao_Impl(this)
  }

  public override val draftDao: DraftDao
    get() = _draftDao.value

  private val _conflictDao: Lazy<ConflictDao> = lazy {
    ConflictDao_Impl(this)
  }

  public override val conflictDao: ConflictDao
    get() = _conflictDao.value

  private val _alertDao: Lazy<AlertDao> = lazy {
    AlertDao_Impl(this)
  }

  public override val alertDao: AlertDao
    get() = _alertDao.value

  private val _billReminderDao: Lazy<BillReminderDao> = lazy {
    BillReminderDao_Impl(this)
  }

  public override val billReminderDao: BillReminderDao
    get() = _billReminderDao.value

  private val _cloudTodoDao: Lazy<CloudTodoDao> = lazy {
    CloudTodoDao_Impl(this)
  }

  public override val cloudTodoDao: CloudTodoDao
    get() = _cloudTodoDao.value

  private val _coinDetailDao: Lazy<CoinDetailDao> = lazy {
    CoinDetailDao_Impl(this)
  }

  public override val coinDetailDao: CoinDetailDao
    get() = _coinDetailDao.value

  private val _coinMarketDao: Lazy<CoinMarketDao> = lazy {
    CoinMarketDao_Impl(this)
  }

  public override val coinMarketDao: CoinMarketDao
    get() = _coinMarketDao.value

  private val _exchangeRatesDao: Lazy<ExchangeRatesDao> = lazy {
    ExchangeRatesDao_Impl(this)
  }

  public override val exchangeRatesDao: ExchangeRatesDao
    get() = _exchangeRatesDao.value

  private val _interestRateSeriesDao: Lazy<InterestRateSeriesDao> = lazy {
    InterestRateSeriesDao_Impl(this)
  }

  public override val interestRateSeriesDao: InterestRateSeriesDao
    get() = _interestRateSeriesDao.value

  private val _loanDao: Lazy<LoanDao> = lazy {
    LoanDao_Impl(this)
  }

  public override val loanDao: LoanDao
    get() = _loanDao.value

  private val _rateHistoryDao: Lazy<RateHistoryDao> = lazy {
    RateHistoryDao_Impl(this)
  }

  public override val rateHistoryDao: RateHistoryDao
    get() = _rateHistoryDao.value

  private val _watchlistDao: Lazy<WatchlistDao> = lazy {
    WatchlistDao_Impl(this)
  }

  public override val watchlistDao: WatchlistDao
    get() = _watchlistDao.value

  protected override fun createOpenDelegate(): RoomOpenDelegate {
    val _openDelegate: RoomOpenDelegate = object : RoomOpenDelegate(13, "bbcd928bb6837728fcc10878287a6192", "df939e62c3dca5c0112fc37f21afbffd") {
      public override suspend fun createAllTables(connection: SQLiteConnection) {
        connection.execSQL("CREATE TABLE IF NOT EXISTS `store_bookkeeper` (`key` TEXT NOT NULL, `lastFailedSync` INTEGER NOT NULL, PRIMARY KEY(`key`))")
        connection.execSQL("CREATE TABLE IF NOT EXISTS `framework_fetched_at` (`storeKey` TEXT NOT NULL, `lastFetchedMillis` INTEGER NOT NULL, PRIMARY KEY(`storeKey`))")
        connection.execSQL("CREATE TABLE IF NOT EXISTS `framework_submit_drafts` (`id` INTEGER PRIMARY KEY AUTOINCREMENT NOT NULL, `formKey` TEXT NOT NULL, `uniqueKey` TEXT, `payloadJson` TEXT NOT NULL, `status` TEXT NOT NULL, `createdAtMs` INTEGER NOT NULL, `updatedAtMs` INTEGER NOT NULL, `errorMessage` TEXT, `attemptCount` INTEGER NOT NULL DEFAULT 0)")
        connection.execSQL("CREATE TABLE IF NOT EXISTS `framework_write_conflicts` (`id` INTEGER PRIMARY KEY AUTOINCREMENT NOT NULL, `entity` TEXT NOT NULL, `entityKey` TEXT NOT NULL, `localPayloadJson` TEXT NOT NULL, `serverPayloadJson` TEXT NOT NULL, `formRoute` TEXT, `recordedAtMs` INTEGER NOT NULL, `resolved` INTEGER NOT NULL)")
        connection.execSQL("CREATE TABLE IF NOT EXISTS `alerts` (`id` TEXT NOT NULL, `symbol` TEXT NOT NULL, `targetPrice` REAL NOT NULL, `conditionAbove` INTEGER NOT NULL, `createdAt` INTEGER NOT NULL, PRIMARY KEY(`id`))")
        connection.execSQL("CREATE TABLE IF NOT EXISTS `banking_bill_reminders` (`id` TEXT NOT NULL, `name` TEXT NOT NULL, `amount` REAL NOT NULL, `dueDay` INTEGER NOT NULL, `recurrence` TEXT NOT NULL, `category` TEXT NOT NULL, `enabled` INTEGER NOT NULL, `reminderDaysBefore` INTEGER NOT NULL, `createdAtMs` INTEGER NOT NULL, `updatedAtMs` INTEGER NOT NULL, PRIMARY KEY(`id`))")
        connection.execSQL("CREATE TABLE IF NOT EXISTS `banking_loans` (`id` TEXT NOT NULL, `name` TEXT NOT NULL, `kind` TEXT NOT NULL, `principal` REAL NOT NULL, `principalRemaining` REAL NOT NULL, `annualRatePercent` REAL NOT NULL, `tenureMonths` INTEGER NOT NULL, `monthsRemaining` INTEGER NOT NULL, `monthlyPayment` REAL NOT NULL, `nextDueDate` TEXT NOT NULL, `totalPaid` REAL NOT NULL, `createdAtMs` INTEGER NOT NULL, `updatedAtMs` INTEGER NOT NULL, PRIMARY KEY(`id`))")
        connection.execSQL("CREATE TABLE IF NOT EXISTS `cloud_todos` (`id` INTEGER NOT NULL, `title` TEXT NOT NULL, `completed` INTEGER NOT NULL, PRIMARY KEY(`id`))")
        connection.execSQL("CREATE TABLE IF NOT EXISTS `coin_detail` (`id` TEXT NOT NULL, `name` TEXT NOT NULL, `symbol` TEXT NOT NULL, `imageUrl` TEXT NOT NULL, `currentPrice` REAL NOT NULL, `marketCap` INTEGER NOT NULL, `marketCapRank` INTEGER NOT NULL, `priceChangePercent24h` REAL NOT NULL, `high24h` REAL NOT NULL, `low24h` REAL NOT NULL, `circulatingSupply` REAL NOT NULL, `maxSupply` REAL, `description` TEXT NOT NULL, `fetchedAt` INTEGER NOT NULL, PRIMARY KEY(`id`))")
        connection.execSQL("CREATE TABLE IF NOT EXISTS `coin_markets` (`id` TEXT NOT NULL, `symbol` TEXT NOT NULL, `name` TEXT NOT NULL, `imageUrl` TEXT NOT NULL, `currentPrice` REAL NOT NULL, `marketCap` INTEGER NOT NULL, `marketCapRank` INTEGER NOT NULL, `priceChangePercent24h` REAL NOT NULL, `high24h` REAL NOT NULL, `low24h` REAL NOT NULL, `page` INTEGER NOT NULL, `fetchedAt` INTEGER NOT NULL, PRIMARY KEY(`id`))")
        connection.execSQL("CREATE TABLE IF NOT EXISTS `exchange_rates` (`baseCurrency` TEXT NOT NULL, `date` TEXT NOT NULL, `ratesJson` TEXT NOT NULL, `fetchedAt` INTEGER NOT NULL, PRIMARY KEY(`baseCurrency`))")
        connection.execSQL("CREATE TABLE IF NOT EXISTS `rate_history` (`fromCurrency` TEXT NOT NULL, `toCurrency` TEXT NOT NULL, `startDate` TEXT NOT NULL, `endDate` TEXT NOT NULL, `ratesJson` TEXT NOT NULL, `fetchedAt` INTEGER NOT NULL, PRIMARY KEY(`fromCurrency`, `toCurrency`, `startDate`, `endDate`))")
        connection.execSQL("CREATE TABLE IF NOT EXISTS `interest_rate_series` (`seriesId` TEXT NOT NULL, `date` TEXT NOT NULL, `value` REAL NOT NULL, `updatedAt` INTEGER NOT NULL, PRIMARY KEY(`seriesId`, `date`))")
        connection.execSQL("CREATE TABLE IF NOT EXISTS `personal_watchlist` (`coinId` TEXT NOT NULL, `addedAtMs` INTEGER NOT NULL, PRIMARY KEY(`coinId`))")
        connection.execSQL("CREATE TABLE IF NOT EXISTS room_master_table (id INTEGER PRIMARY KEY,identity_hash TEXT)")
        connection.execSQL("INSERT OR REPLACE INTO room_master_table (id,identity_hash) VALUES(42, 'bbcd928bb6837728fcc10878287a6192')")
      }

      public override suspend fun dropAllTables(connection: SQLiteConnection) {
        connection.execSQL("DROP TABLE IF EXISTS `store_bookkeeper`")
        connection.execSQL("DROP TABLE IF EXISTS `framework_fetched_at`")
        connection.execSQL("DROP TABLE IF EXISTS `framework_submit_drafts`")
        connection.execSQL("DROP TABLE IF EXISTS `framework_write_conflicts`")
        connection.execSQL("DROP TABLE IF EXISTS `alerts`")
        connection.execSQL("DROP TABLE IF EXISTS `banking_bill_reminders`")
        connection.execSQL("DROP TABLE IF EXISTS `banking_loans`")
        connection.execSQL("DROP TABLE IF EXISTS `cloud_todos`")
        connection.execSQL("DROP TABLE IF EXISTS `coin_detail`")
        connection.execSQL("DROP TABLE IF EXISTS `coin_markets`")
        connection.execSQL("DROP TABLE IF EXISTS `exchange_rates`")
        connection.execSQL("DROP TABLE IF EXISTS `rate_history`")
        connection.execSQL("DROP TABLE IF EXISTS `interest_rate_series`")
        connection.execSQL("DROP TABLE IF EXISTS `personal_watchlist`")
      }

      public override suspend fun onCreate(connection: SQLiteConnection) {
      }

      public override suspend fun onOpen(connection: SQLiteConnection) {
        internalInitInvalidationTracker(connection)
      }

      public override suspend fun onPreMigrate(connection: SQLiteConnection) {
        dropFtsSyncTriggers(connection)
      }

      public override suspend fun onPostMigrate(connection: SQLiteConnection) {
      }

      public override suspend fun onValidateSchema(connection: SQLiteConnection): RoomOpenDelegate.ValidationResult {
        val _columnsStoreBookkeeper: MutableMap<String, TableInfo.Column> = mutableMapOf()
        _columnsStoreBookkeeper.put("key", TableInfo.Column("key", "TEXT", true, 1, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsStoreBookkeeper.put("lastFailedSync", TableInfo.Column("lastFailedSync", "INTEGER", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        val _foreignKeysStoreBookkeeper: MutableSet<TableInfo.ForeignKey> = mutableSetOf()
        val _indicesStoreBookkeeper: MutableSet<TableInfo.Index> = mutableSetOf()
        val _infoStoreBookkeeper: TableInfo = TableInfo("store_bookkeeper", _columnsStoreBookkeeper, _foreignKeysStoreBookkeeper, _indicesStoreBookkeeper)
        val _existingStoreBookkeeper: TableInfo = read(connection, "store_bookkeeper")
        if (!_infoStoreBookkeeper.equals(_existingStoreBookkeeper)) {
          return RoomOpenDelegate.ValidationResult(false, """
              |store_bookkeeper(kpt.core.base.database.infra.entity.BookkeeperEntity).
              | Expected:
              |""".trimMargin() + _infoStoreBookkeeper + """
              |
              | Found:
              |""".trimMargin() + _existingStoreBookkeeper)
        }
        val _columnsFrameworkFetchedAt: MutableMap<String, TableInfo.Column> = mutableMapOf()
        _columnsFrameworkFetchedAt.put("storeKey", TableInfo.Column("storeKey", "TEXT", true, 1, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsFrameworkFetchedAt.put("lastFetchedMillis", TableInfo.Column("lastFetchedMillis", "INTEGER", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        val _foreignKeysFrameworkFetchedAt: MutableSet<TableInfo.ForeignKey> = mutableSetOf()
        val _indicesFrameworkFetchedAt: MutableSet<TableInfo.Index> = mutableSetOf()
        val _infoFrameworkFetchedAt: TableInfo = TableInfo("framework_fetched_at", _columnsFrameworkFetchedAt, _foreignKeysFrameworkFetchedAt, _indicesFrameworkFetchedAt)
        val _existingFrameworkFetchedAt: TableInfo = read(connection, "framework_fetched_at")
        if (!_infoFrameworkFetchedAt.equals(_existingFrameworkFetchedAt)) {
          return RoomOpenDelegate.ValidationResult(false, """
              |framework_fetched_at(kpt.core.base.database.infra.entity.FetchedAtEntity).
              | Expected:
              |""".trimMargin() + _infoFrameworkFetchedAt + """
              |
              | Found:
              |""".trimMargin() + _existingFrameworkFetchedAt)
        }
        val _columnsFrameworkSubmitDrafts: MutableMap<String, TableInfo.Column> = mutableMapOf()
        _columnsFrameworkSubmitDrafts.put("id", TableInfo.Column("id", "INTEGER", true, 1, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsFrameworkSubmitDrafts.put("formKey", TableInfo.Column("formKey", "TEXT", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsFrameworkSubmitDrafts.put("uniqueKey", TableInfo.Column("uniqueKey", "TEXT", false, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsFrameworkSubmitDrafts.put("payloadJson", TableInfo.Column("payloadJson", "TEXT", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsFrameworkSubmitDrafts.put("status", TableInfo.Column("status", "TEXT", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsFrameworkSubmitDrafts.put("createdAtMs", TableInfo.Column("createdAtMs", "INTEGER", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsFrameworkSubmitDrafts.put("updatedAtMs", TableInfo.Column("updatedAtMs", "INTEGER", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsFrameworkSubmitDrafts.put("errorMessage", TableInfo.Column("errorMessage", "TEXT", false, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsFrameworkSubmitDrafts.put("attemptCount", TableInfo.Column("attemptCount", "INTEGER", true, 0, "0", TableInfo.CREATED_FROM_ENTITY))
        val _foreignKeysFrameworkSubmitDrafts: MutableSet<TableInfo.ForeignKey> = mutableSetOf()
        val _indicesFrameworkSubmitDrafts: MutableSet<TableInfo.Index> = mutableSetOf()
        val _infoFrameworkSubmitDrafts: TableInfo = TableInfo("framework_submit_drafts", _columnsFrameworkSubmitDrafts, _foreignKeysFrameworkSubmitDrafts, _indicesFrameworkSubmitDrafts)
        val _existingFrameworkSubmitDrafts: TableInfo = read(connection, "framework_submit_drafts")
        if (!_infoFrameworkSubmitDrafts.equals(_existingFrameworkSubmitDrafts)) {
          return RoomOpenDelegate.ValidationResult(false, """
              |framework_submit_drafts(kpt.core.base.database.infra.entity.DraftEntity).
              | Expected:
              |""".trimMargin() + _infoFrameworkSubmitDrafts + """
              |
              | Found:
              |""".trimMargin() + _existingFrameworkSubmitDrafts)
        }
        val _columnsFrameworkWriteConflicts: MutableMap<String, TableInfo.Column> = mutableMapOf()
        _columnsFrameworkWriteConflicts.put("id", TableInfo.Column("id", "INTEGER", true, 1, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsFrameworkWriteConflicts.put("entity", TableInfo.Column("entity", "TEXT", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsFrameworkWriteConflicts.put("entityKey", TableInfo.Column("entityKey", "TEXT", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsFrameworkWriteConflicts.put("localPayloadJson", TableInfo.Column("localPayloadJson", "TEXT", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsFrameworkWriteConflicts.put("serverPayloadJson", TableInfo.Column("serverPayloadJson", "TEXT", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsFrameworkWriteConflicts.put("formRoute", TableInfo.Column("formRoute", "TEXT", false, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsFrameworkWriteConflicts.put("recordedAtMs", TableInfo.Column("recordedAtMs", "INTEGER", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsFrameworkWriteConflicts.put("resolved", TableInfo.Column("resolved", "INTEGER", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        val _foreignKeysFrameworkWriteConflicts: MutableSet<TableInfo.ForeignKey> = mutableSetOf()
        val _indicesFrameworkWriteConflicts: MutableSet<TableInfo.Index> = mutableSetOf()
        val _infoFrameworkWriteConflicts: TableInfo = TableInfo("framework_write_conflicts", _columnsFrameworkWriteConflicts, _foreignKeysFrameworkWriteConflicts, _indicesFrameworkWriteConflicts)
        val _existingFrameworkWriteConflicts: TableInfo = read(connection, "framework_write_conflicts")
        if (!_infoFrameworkWriteConflicts.equals(_existingFrameworkWriteConflicts)) {
          return RoomOpenDelegate.ValidationResult(false, """
              |framework_write_conflicts(kpt.core.base.database.infra.entity.ConflictEntity).
              | Expected:
              |""".trimMargin() + _infoFrameworkWriteConflicts + """
              |
              | Found:
              |""".trimMargin() + _existingFrameworkWriteConflicts)
        }
        val _columnsAlerts: MutableMap<String, TableInfo.Column> = mutableMapOf()
        _columnsAlerts.put("id", TableInfo.Column("id", "TEXT", true, 1, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsAlerts.put("symbol", TableInfo.Column("symbol", "TEXT", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsAlerts.put("targetPrice", TableInfo.Column("targetPrice", "REAL", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsAlerts.put("conditionAbove", TableInfo.Column("conditionAbove", "INTEGER", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsAlerts.put("createdAt", TableInfo.Column("createdAt", "INTEGER", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        val _foreignKeysAlerts: MutableSet<TableInfo.ForeignKey> = mutableSetOf()
        val _indicesAlerts: MutableSet<TableInfo.Index> = mutableSetOf()
        val _infoAlerts: TableInfo = TableInfo("alerts", _columnsAlerts, _foreignKeysAlerts, _indicesAlerts)
        val _existingAlerts: TableInfo = read(connection, "alerts")
        if (!_infoAlerts.equals(_existingAlerts)) {
          return RoomOpenDelegate.ValidationResult(false, """
              |alerts(kpt.core.database.alerts.AlertEntity).
              | Expected:
              |""".trimMargin() + _infoAlerts + """
              |
              | Found:
              |""".trimMargin() + _existingAlerts)
        }
        val _columnsBankingBillReminders: MutableMap<String, TableInfo.Column> = mutableMapOf()
        _columnsBankingBillReminders.put("id", TableInfo.Column("id", "TEXT", true, 1, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsBankingBillReminders.put("name", TableInfo.Column("name", "TEXT", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsBankingBillReminders.put("amount", TableInfo.Column("amount", "REAL", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsBankingBillReminders.put("dueDay", TableInfo.Column("dueDay", "INTEGER", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsBankingBillReminders.put("recurrence", TableInfo.Column("recurrence", "TEXT", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsBankingBillReminders.put("category", TableInfo.Column("category", "TEXT", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsBankingBillReminders.put("enabled", TableInfo.Column("enabled", "INTEGER", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsBankingBillReminders.put("reminderDaysBefore", TableInfo.Column("reminderDaysBefore", "INTEGER", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsBankingBillReminders.put("createdAtMs", TableInfo.Column("createdAtMs", "INTEGER", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsBankingBillReminders.put("updatedAtMs", TableInfo.Column("updatedAtMs", "INTEGER", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        val _foreignKeysBankingBillReminders: MutableSet<TableInfo.ForeignKey> = mutableSetOf()
        val _indicesBankingBillReminders: MutableSet<TableInfo.Index> = mutableSetOf()
        val _infoBankingBillReminders: TableInfo = TableInfo("banking_bill_reminders", _columnsBankingBillReminders, _foreignKeysBankingBillReminders, _indicesBankingBillReminders)
        val _existingBankingBillReminders: TableInfo = read(connection, "banking_bill_reminders")
        if (!_infoBankingBillReminders.equals(_existingBankingBillReminders)) {
          return RoomOpenDelegate.ValidationResult(false, """
              |banking_bill_reminders(kpt.core.database.banking.entity.BillReminderEntity).
              | Expected:
              |""".trimMargin() + _infoBankingBillReminders + """
              |
              | Found:
              |""".trimMargin() + _existingBankingBillReminders)
        }
        val _columnsBankingLoans: MutableMap<String, TableInfo.Column> = mutableMapOf()
        _columnsBankingLoans.put("id", TableInfo.Column("id", "TEXT", true, 1, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsBankingLoans.put("name", TableInfo.Column("name", "TEXT", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsBankingLoans.put("kind", TableInfo.Column("kind", "TEXT", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsBankingLoans.put("principal", TableInfo.Column("principal", "REAL", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsBankingLoans.put("principalRemaining", TableInfo.Column("principalRemaining", "REAL", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsBankingLoans.put("annualRatePercent", TableInfo.Column("annualRatePercent", "REAL", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsBankingLoans.put("tenureMonths", TableInfo.Column("tenureMonths", "INTEGER", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsBankingLoans.put("monthsRemaining", TableInfo.Column("monthsRemaining", "INTEGER", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsBankingLoans.put("monthlyPayment", TableInfo.Column("monthlyPayment", "REAL", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsBankingLoans.put("nextDueDate", TableInfo.Column("nextDueDate", "TEXT", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsBankingLoans.put("totalPaid", TableInfo.Column("totalPaid", "REAL", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsBankingLoans.put("createdAtMs", TableInfo.Column("createdAtMs", "INTEGER", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsBankingLoans.put("updatedAtMs", TableInfo.Column("updatedAtMs", "INTEGER", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        val _foreignKeysBankingLoans: MutableSet<TableInfo.ForeignKey> = mutableSetOf()
        val _indicesBankingLoans: MutableSet<TableInfo.Index> = mutableSetOf()
        val _infoBankingLoans: TableInfo = TableInfo("banking_loans", _columnsBankingLoans, _foreignKeysBankingLoans, _indicesBankingLoans)
        val _existingBankingLoans: TableInfo = read(connection, "banking_loans")
        if (!_infoBankingLoans.equals(_existingBankingLoans)) {
          return RoomOpenDelegate.ValidationResult(false, """
              |banking_loans(kpt.core.database.banking.entity.LoanEntity).
              | Expected:
              |""".trimMargin() + _infoBankingLoans + """
              |
              | Found:
              |""".trimMargin() + _existingBankingLoans)
        }
        val _columnsCloudTodos: MutableMap<String, TableInfo.Column> = mutableMapOf()
        _columnsCloudTodos.put("id", TableInfo.Column("id", "INTEGER", true, 1, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsCloudTodos.put("title", TableInfo.Column("title", "TEXT", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsCloudTodos.put("completed", TableInfo.Column("completed", "INTEGER", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        val _foreignKeysCloudTodos: MutableSet<TableInfo.ForeignKey> = mutableSetOf()
        val _indicesCloudTodos: MutableSet<TableInfo.Index> = mutableSetOf()
        val _infoCloudTodos: TableInfo = TableInfo("cloud_todos", _columnsCloudTodos, _foreignKeysCloudTodos, _indicesCloudTodos)
        val _existingCloudTodos: TableInfo = read(connection, "cloud_todos")
        if (!_infoCloudTodos.equals(_existingCloudTodos)) {
          return RoomOpenDelegate.ValidationResult(false, """
              |cloud_todos(kpt.core.database.cloudtodo.CloudTodoEntity).
              | Expected:
              |""".trimMargin() + _infoCloudTodos + """
              |
              | Found:
              |""".trimMargin() + _existingCloudTodos)
        }
        val _columnsCoinDetail: MutableMap<String, TableInfo.Column> = mutableMapOf()
        _columnsCoinDetail.put("id", TableInfo.Column("id", "TEXT", true, 1, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsCoinDetail.put("name", TableInfo.Column("name", "TEXT", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsCoinDetail.put("symbol", TableInfo.Column("symbol", "TEXT", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsCoinDetail.put("imageUrl", TableInfo.Column("imageUrl", "TEXT", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsCoinDetail.put("currentPrice", TableInfo.Column("currentPrice", "REAL", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsCoinDetail.put("marketCap", TableInfo.Column("marketCap", "INTEGER", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsCoinDetail.put("marketCapRank", TableInfo.Column("marketCapRank", "INTEGER", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsCoinDetail.put("priceChangePercent24h", TableInfo.Column("priceChangePercent24h", "REAL", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsCoinDetail.put("high24h", TableInfo.Column("high24h", "REAL", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsCoinDetail.put("low24h", TableInfo.Column("low24h", "REAL", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsCoinDetail.put("circulatingSupply", TableInfo.Column("circulatingSupply", "REAL", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsCoinDetail.put("maxSupply", TableInfo.Column("maxSupply", "REAL", false, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsCoinDetail.put("description", TableInfo.Column("description", "TEXT", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsCoinDetail.put("fetchedAt", TableInfo.Column("fetchedAt", "INTEGER", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        val _foreignKeysCoinDetail: MutableSet<TableInfo.ForeignKey> = mutableSetOf()
        val _indicesCoinDetail: MutableSet<TableInfo.Index> = mutableSetOf()
        val _infoCoinDetail: TableInfo = TableInfo("coin_detail", _columnsCoinDetail, _foreignKeysCoinDetail, _indicesCoinDetail)
        val _existingCoinDetail: TableInfo = read(connection, "coin_detail")
        if (!_infoCoinDetail.equals(_existingCoinDetail)) {
          return RoomOpenDelegate.ValidationResult(false, """
              |coin_detail(kpt.core.database.crypto.entity.CoinDetailEntity).
              | Expected:
              |""".trimMargin() + _infoCoinDetail + """
              |
              | Found:
              |""".trimMargin() + _existingCoinDetail)
        }
        val _columnsCoinMarkets: MutableMap<String, TableInfo.Column> = mutableMapOf()
        _columnsCoinMarkets.put("id", TableInfo.Column("id", "TEXT", true, 1, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsCoinMarkets.put("symbol", TableInfo.Column("symbol", "TEXT", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsCoinMarkets.put("name", TableInfo.Column("name", "TEXT", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsCoinMarkets.put("imageUrl", TableInfo.Column("imageUrl", "TEXT", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsCoinMarkets.put("currentPrice", TableInfo.Column("currentPrice", "REAL", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsCoinMarkets.put("marketCap", TableInfo.Column("marketCap", "INTEGER", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsCoinMarkets.put("marketCapRank", TableInfo.Column("marketCapRank", "INTEGER", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsCoinMarkets.put("priceChangePercent24h", TableInfo.Column("priceChangePercent24h", "REAL", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsCoinMarkets.put("high24h", TableInfo.Column("high24h", "REAL", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsCoinMarkets.put("low24h", TableInfo.Column("low24h", "REAL", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsCoinMarkets.put("page", TableInfo.Column("page", "INTEGER", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsCoinMarkets.put("fetchedAt", TableInfo.Column("fetchedAt", "INTEGER", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        val _foreignKeysCoinMarkets: MutableSet<TableInfo.ForeignKey> = mutableSetOf()
        val _indicesCoinMarkets: MutableSet<TableInfo.Index> = mutableSetOf()
        val _infoCoinMarkets: TableInfo = TableInfo("coin_markets", _columnsCoinMarkets, _foreignKeysCoinMarkets, _indicesCoinMarkets)
        val _existingCoinMarkets: TableInfo = read(connection, "coin_markets")
        if (!_infoCoinMarkets.equals(_existingCoinMarkets)) {
          return RoomOpenDelegate.ValidationResult(false, """
              |coin_markets(kpt.core.database.crypto.entity.CoinMarketEntity).
              | Expected:
              |""".trimMargin() + _infoCoinMarkets + """
              |
              | Found:
              |""".trimMargin() + _existingCoinMarkets)
        }
        val _columnsExchangeRates: MutableMap<String, TableInfo.Column> = mutableMapOf()
        _columnsExchangeRates.put("baseCurrency", TableInfo.Column("baseCurrency", "TEXT", true, 1, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsExchangeRates.put("date", TableInfo.Column("date", "TEXT", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsExchangeRates.put("ratesJson", TableInfo.Column("ratesJson", "TEXT", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsExchangeRates.put("fetchedAt", TableInfo.Column("fetchedAt", "INTEGER", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        val _foreignKeysExchangeRates: MutableSet<TableInfo.ForeignKey> = mutableSetOf()
        val _indicesExchangeRates: MutableSet<TableInfo.Index> = mutableSetOf()
        val _infoExchangeRates: TableInfo = TableInfo("exchange_rates", _columnsExchangeRates, _foreignKeysExchangeRates, _indicesExchangeRates)
        val _existingExchangeRates: TableInfo = read(connection, "exchange_rates")
        if (!_infoExchangeRates.equals(_existingExchangeRates)) {
          return RoomOpenDelegate.ValidationResult(false, """
              |exchange_rates(kpt.core.database.currency.entity.ExchangeRatesEntity).
              | Expected:
              |""".trimMargin() + _infoExchangeRates + """
              |
              | Found:
              |""".trimMargin() + _existingExchangeRates)
        }
        val _columnsRateHistory: MutableMap<String, TableInfo.Column> = mutableMapOf()
        _columnsRateHistory.put("fromCurrency", TableInfo.Column("fromCurrency", "TEXT", true, 1, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsRateHistory.put("toCurrency", TableInfo.Column("toCurrency", "TEXT", true, 2, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsRateHistory.put("startDate", TableInfo.Column("startDate", "TEXT", true, 3, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsRateHistory.put("endDate", TableInfo.Column("endDate", "TEXT", true, 4, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsRateHistory.put("ratesJson", TableInfo.Column("ratesJson", "TEXT", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsRateHistory.put("fetchedAt", TableInfo.Column("fetchedAt", "INTEGER", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        val _foreignKeysRateHistory: MutableSet<TableInfo.ForeignKey> = mutableSetOf()
        val _indicesRateHistory: MutableSet<TableInfo.Index> = mutableSetOf()
        val _infoRateHistory: TableInfo = TableInfo("rate_history", _columnsRateHistory, _foreignKeysRateHistory, _indicesRateHistory)
        val _existingRateHistory: TableInfo = read(connection, "rate_history")
        if (!_infoRateHistory.equals(_existingRateHistory)) {
          return RoomOpenDelegate.ValidationResult(false, """
              |rate_history(kpt.core.database.currency.entity.RateHistoryEntity).
              | Expected:
              |""".trimMargin() + _infoRateHistory + """
              |
              | Found:
              |""".trimMargin() + _existingRateHistory)
        }
        val _columnsInterestRateSeries: MutableMap<String, TableInfo.Column> = mutableMapOf()
        _columnsInterestRateSeries.put("seriesId", TableInfo.Column("seriesId", "TEXT", true, 1, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsInterestRateSeries.put("date", TableInfo.Column("date", "TEXT", true, 2, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsInterestRateSeries.put("value", TableInfo.Column("value", "REAL", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsInterestRateSeries.put("updatedAt", TableInfo.Column("updatedAt", "INTEGER", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        val _foreignKeysInterestRateSeries: MutableSet<TableInfo.ForeignKey> = mutableSetOf()
        val _indicesInterestRateSeries: MutableSet<TableInfo.Index> = mutableSetOf()
        val _infoInterestRateSeries: TableInfo = TableInfo("interest_rate_series", _columnsInterestRateSeries, _foreignKeysInterestRateSeries, _indicesInterestRateSeries)
        val _existingInterestRateSeries: TableInfo = read(connection, "interest_rate_series")
        if (!_infoInterestRateSeries.equals(_existingInterestRateSeries)) {
          return RoomOpenDelegate.ValidationResult(false, """
              |interest_rate_series(kpt.core.database.economic.InterestRateSeriesEntity).
              | Expected:
              |""".trimMargin() + _infoInterestRateSeries + """
              |
              | Found:
              |""".trimMargin() + _existingInterestRateSeries)
        }
        val _columnsPersonalWatchlist: MutableMap<String, TableInfo.Column> = mutableMapOf()
        _columnsPersonalWatchlist.put("coinId", TableInfo.Column("coinId", "TEXT", true, 1, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsPersonalWatchlist.put("addedAtMs", TableInfo.Column("addedAtMs", "INTEGER", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        val _foreignKeysPersonalWatchlist: MutableSet<TableInfo.ForeignKey> = mutableSetOf()
        val _indicesPersonalWatchlist: MutableSet<TableInfo.Index> = mutableSetOf()
        val _infoPersonalWatchlist: TableInfo = TableInfo("personal_watchlist", _columnsPersonalWatchlist, _foreignKeysPersonalWatchlist, _indicesPersonalWatchlist)
        val _existingPersonalWatchlist: TableInfo = read(connection, "personal_watchlist")
        if (!_infoPersonalWatchlist.equals(_existingPersonalWatchlist)) {
          return RoomOpenDelegate.ValidationResult(false, """
              |personal_watchlist(kpt.core.database.watchlist.entity.WatchlistEntity).
              | Expected:
              |""".trimMargin() + _infoPersonalWatchlist + """
              |
              | Found:
              |""".trimMargin() + _existingPersonalWatchlist)
        }
        return RoomOpenDelegate.ValidationResult(true, null)
      }
    }
    return _openDelegate
  }

  protected override fun createInvalidationTracker(): InvalidationTracker {
    val _shadowTablesMap: MutableMap<String, String> = mutableMapOf()
    val _viewTables: MutableMap<String, Set<String>> = mutableMapOf()
    return InvalidationTracker(this, _shadowTablesMap, _viewTables, "store_bookkeeper", "framework_fetched_at", "framework_submit_drafts", "framework_write_conflicts", "alerts", "banking_bill_reminders", "banking_loans", "cloud_todos", "coin_detail", "coin_markets", "exchange_rates", "rate_history", "interest_rate_series", "personal_watchlist")
  }

  public override suspend fun clearAllTables() {
    performClear(this, false, "store_bookkeeper", "framework_fetched_at", "framework_submit_drafts", "framework_write_conflicts", "alerts", "banking_bill_reminders", "banking_loans", "cloud_todos", "coin_detail", "coin_markets", "exchange_rates", "rate_history", "interest_rate_series", "personal_watchlist")
  }

  protected override fun getRequiredColumnTypeConverterClasses(): Map<KClass<*>, List<KClass<*>>> {
    val _columnTypeConvertersMap: MutableMap<KClass<*>, List<KClass<*>>> = mutableMapOf()
    _columnTypeConvertersMap.put(BookkeeperDao::class, BookkeeperDao_Impl.getRequiredColumnConverters())
    _columnTypeConvertersMap.put(FetchedAtDao::class, FetchedAtDao_Impl.getRequiredColumnConverters())
    _columnTypeConvertersMap.put(DraftDao::class, DraftDao_Impl.getRequiredColumnConverters())
    _columnTypeConvertersMap.put(ConflictDao::class, ConflictDao_Impl.getRequiredColumnConverters())
    _columnTypeConvertersMap.put(AlertDao::class, AlertDao_Impl.getRequiredColumnConverters())
    _columnTypeConvertersMap.put(BillReminderDao::class, BillReminderDao_Impl.getRequiredColumnConverters())
    _columnTypeConvertersMap.put(CloudTodoDao::class, CloudTodoDao_Impl.getRequiredColumnConverters())
    _columnTypeConvertersMap.put(CoinDetailDao::class, CoinDetailDao_Impl.getRequiredColumnConverters())
    _columnTypeConvertersMap.put(CoinMarketDao::class, CoinMarketDao_Impl.getRequiredColumnConverters())
    _columnTypeConvertersMap.put(ExchangeRatesDao::class, ExchangeRatesDao_Impl.getRequiredColumnConverters())
    _columnTypeConvertersMap.put(InterestRateSeriesDao::class, InterestRateSeriesDao_Impl.getRequiredColumnConverters())
    _columnTypeConvertersMap.put(LoanDao::class, LoanDao_Impl.getRequiredColumnConverters())
    _columnTypeConvertersMap.put(RateHistoryDao::class, RateHistoryDao_Impl.getRequiredColumnConverters())
    _columnTypeConvertersMap.put(WatchlistDao::class, WatchlistDao_Impl.getRequiredColumnConverters())
    return _columnTypeConvertersMap
  }

  protected override fun getRequiredDaoReturnTypeConverterClasses(): Map<KClass<*>, List<KClass<*>>> {
    val _daoReturnTypeConvertersMap: MutableMap<KClass<*>, List<KClass<*>>> = mutableMapOf()
    _daoReturnTypeConvertersMap.put(BookkeeperDao::class, BookkeeperDao_Impl.getRequiredDaoReturnTypeConverters())
    _daoReturnTypeConvertersMap.put(FetchedAtDao::class, FetchedAtDao_Impl.getRequiredDaoReturnTypeConverters())
    _daoReturnTypeConvertersMap.put(DraftDao::class, DraftDao_Impl.getRequiredDaoReturnTypeConverters())
    _daoReturnTypeConvertersMap.put(ConflictDao::class, ConflictDao_Impl.getRequiredDaoReturnTypeConverters())
    _daoReturnTypeConvertersMap.put(AlertDao::class, AlertDao_Impl.getRequiredDaoReturnTypeConverters())
    _daoReturnTypeConvertersMap.put(BillReminderDao::class, BillReminderDao_Impl.getRequiredDaoReturnTypeConverters())
    _daoReturnTypeConvertersMap.put(CloudTodoDao::class, CloudTodoDao_Impl.getRequiredDaoReturnTypeConverters())
    _daoReturnTypeConvertersMap.put(CoinDetailDao::class, CoinDetailDao_Impl.getRequiredDaoReturnTypeConverters())
    _daoReturnTypeConvertersMap.put(CoinMarketDao::class, CoinMarketDao_Impl.getRequiredDaoReturnTypeConverters())
    _daoReturnTypeConvertersMap.put(ExchangeRatesDao::class, ExchangeRatesDao_Impl.getRequiredDaoReturnTypeConverters())
    _daoReturnTypeConvertersMap.put(InterestRateSeriesDao::class, InterestRateSeriesDao_Impl.getRequiredDaoReturnTypeConverters())
    _daoReturnTypeConvertersMap.put(LoanDao::class, LoanDao_Impl.getRequiredDaoReturnTypeConverters())
    _daoReturnTypeConvertersMap.put(RateHistoryDao::class, RateHistoryDao_Impl.getRequiredDaoReturnTypeConverters())
    _daoReturnTypeConvertersMap.put(WatchlistDao::class, WatchlistDao_Impl.getRequiredDaoReturnTypeConverters())
    return _daoReturnTypeConvertersMap
  }

  public override fun getRequiredAutoMigrationSpecClasses(): Set<KClass<out AutoMigrationSpec>> {
    val _autoMigrationSpecsSet: MutableSet<KClass<out AutoMigrationSpec>> = mutableSetOf()
    return _autoMigrationSpecsSet
  }

  public override fun createAutoMigrations(autoMigrationSpecs: Map<KClass<out AutoMigrationSpec>, AutoMigrationSpec>): List<Migration> {
    val _autoMigrations: MutableList<Migration> = mutableListOf()
    _autoMigrations.add(AppDatabase_AutoMigration_3_4_Impl())
    _autoMigrations.add(AppDatabase_AutoMigration_4_5_Impl())
    _autoMigrations.add(AppDatabase_AutoMigration_5_6_Impl())
    _autoMigrations.add(AppDatabase_AutoMigration_6_7_Impl())
    _autoMigrations.add(AppDatabase_AutoMigration_7_8_Impl())
    _autoMigrations.add(AppDatabase_AutoMigration_8_10_Impl())
    _autoMigrations.add(AppDatabase_AutoMigration_10_11_Impl())
    _autoMigrations.add(AppDatabase_AutoMigration_11_12_Impl())
    _autoMigrations.add(AppDatabase_AutoMigration_12_13_Impl())
    return _autoMigrations
  }
}
